import { lsGet, lsSet } from "./storage";

// Pitch time: elements with class .vsl-hide stay hidden until the viewer reaches
// the configured second. An element may override it with data-vsl-delay="600".
// Once revealed, the state is remembered so returning visitors see offers immediately.

const SELECTOR = ".vsl-hide, [data-vsl-delay]";

export class Revealer {
  private done = new WeakSet<Element>();
  private all = false;
  private key: string;

  constructor(
    playerId: string,
    private defaultAt: number,
    /** live = triggered by this playback (not restored from a previous visit). */
    private onReveal: (live: boolean) => void,
  ) {
    this.key = "vsl:rv:" + playerId;
    if (lsGet(this.key)) this.revealAll(false);
  }

  /** data-vsl-delay accepts seconds ("1022") or a clock time ("17:02", "1:02:03"). */
  private delayOf(el: Element): number {
    const d = el.getAttribute("data-vsl-delay")?.trim();
    if (!d) return this.defaultAt;
    const parts = d.split(":").map(Number);
    if (parts.some((p) => !Number.isFinite(p) || p < 0)) return this.defaultAt;
    return parts.reduce((acc, p) => acc * 60 + p, 0);
  }

  private show(el: Element): void {
    if (this.done.has(el)) return;
    this.done.add(el);
    el.classList.remove("vsl-hide");
    el.setAttribute("data-vsl-revealed", "");
    (el as HTMLElement).style.removeProperty("display");
  }

  revealAll(live = true): void {
    if (this.all) return;
    this.all = true;
    document.querySelectorAll(SELECTOR).forEach((el) => this.show(el));
    lsSet(this.key, "1");
    this.onReveal(live);
  }

  /**
   * vturb-compatible rule: player.displayHiddenElements(seconds, [".esconder"], { persist: true }).
   * The selected elements stay hidden (whatever hides them) until `seconds` are watched.
   */
  private rules: {
    at: number;
    selectors: string[];
    persist: boolean;
    done: boolean;
    key: string;
    callback?: () => void;
  }[] = [];

  addRule(at: number, selectors: string[], persist: boolean, callback?: () => void): void {
    const key = `${this.key}:${at}:${selectors.join(",")}`;
    const rule = { at, selectors, persist, done: false, key, callback };
    this.rules.push(rule);
    if (persist && lsGet(key)) this.fireRule(rule, false);
  }

  private fireRule(rule: (typeof this.rules)[number], live = true): void {
    rule.done = true;
    for (const sel of rule.selectors) {
      try {
        document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
          this.show(el);
          // vturb landings hide these via their own CSS (e.g. .esconder{display:none})
          if (getComputedStyle(el).display === "none") el.style.setProperty("display", "block", "important");
        });
      } catch {
        /* invalid selector */
      }
    }
    if (rule.persist) lsSet(rule.key, "1");
    try {
      rule.callback?.();
    } catch {
      /* landing callback errors are not ours */
    }
    this.onReveal(live);
  }

  /** Called on every timeupdate with the furthest position reached. */
  tick(pos: number): void {
    for (const r of this.rules) if (!r.done && pos >= r.at) this.fireRule(r);
    if (this.all) return;
    let pending = 0;
    let revealed = false;
    document.querySelectorAll(SELECTOR).forEach((el) => {
      if (this.done.has(el)) return;
      const at = this.delayOf(el);
      if (at <= 0) return;
      if (pos >= at) {
        this.show(el);
        revealed = true;
      } else pending++;
    });
    if (revealed) this.onReveal(true);
    if (this.defaultAt > 0 && pos >= this.defaultAt && pending === 0) {
      this.all = true;
      lsSet(this.key, "1");
    }
  }
}
